import { UNIT_MEASURE } from '~/core/constants';
import { ProductType } from '~/core/enums';
import { isCountertopProduct } from '~/core/helpers';
import formatUOM from '~/core/helpers/formatUOM';
import { ColorModel, PricedProduct } from '~/core/models';
import { PriceTotalsReturn } from '~/core/utils/price.utils';

export const getPromotionEndDate = (
  products: PricedProduct[]
): string | undefined => {
  const [endDate] = products
    .filter((product) => product.expirationPromotionDate)
    .map((product) => new Date(product.expirationPromotionDate as Date))
    .sort((date1, date2) => date1.getTime() - date2.getTime());

  return endDate && endDate.toLocaleDateString();
};

export const calculateTotalPrice = ({
  pricedProducts,
  selectedColor,
  isSmallJob,
  useMinQtyAreaForPricing,
  smallJobMinimumArea,
}: {
  pricedProducts: PricedProduct[];
  selectedColor?: ColorModel;
  isSmallJob: boolean;
  useMinQtyAreaForPricing: boolean;
  smallJobMinimumArea: number;
}): PriceTotalsReturn => {
  // the minimum sq ft allowed is per-material, e.g.: butcher block is 20
  const [countertopQty, priceWithDiscount, priceWithoutDiscount] =
    pricedProducts.reduce(
      ([countertopQty, priceWithDiscount, priceWithoutDiscount], product) => [
        countertopQty + (isCountertopProduct(product) ? product.quantity : 0),
        priceWithDiscount + product.price,
        priceWithoutDiscount + product.priceWithoutDiscount,
      ],
      [0, 0, 0]
    );

  if (countertopQty <= 0) {
    return {
      priceWithoutDiscount: 0,
      priceWithDiscount: 0,
    };
  }

  if (useMinQtyAreaForPricing) {
    const [unitPrice, unitPriceWithoutDiscount] = selectedColor
      ? [selectedColor.price, selectedColor.priceWithoutDiscount]
      : [0, 0];
    const minQtyDelta = isSmallJob ? smallJobMinimumArea - countertopQty : 0;

    return {
      priceWithoutDiscount:
        priceWithoutDiscount + minQtyDelta * unitPriceWithoutDiscount,
      priceWithDiscount: priceWithDiscount + minQtyDelta * unitPrice,
    };
  }

  return {
    priceWithoutDiscount,
    priceWithDiscount,
  };
};

const productTypeToUnitMeasure: Record<string, string> = {
  [ProductType.EDGE]: UNIT_MEASURE.EDGE,
  [ProductType.CORNER_RADIUS]: UNIT_MEASURE.CORNER_RADIUS,
  [ProductType.CUTOUT]: UNIT_MEASURE.CUTOUT,
  [ProductType.DISHWASHER]: UNIT_MEASURE.DISHWASHER,
  [ProductType.REFRIGERATOR]: UNIT_MEASURE.REFRIGERATOR,
  [ProductType.SINK]: UNIT_MEASURE.SINK,
  [ProductType.SINK_LABOR]: UNIT_MEASURE.SINK_LABOR,
  [ProductType.FAUCET]: UNIT_MEASURE.FAUCET,
  [ProductType.LABOR]: UNIT_MEASURE.LABOR,
  [ProductType.COOKING_APPLIANCE]: UNIT_MEASURE.COOKING_APPLIANCE,
  [ProductType.TEAR_OUT]: UNIT_MEASURE.TEAR_OUT,
  [ProductType.WARRANTY]: UNIT_MEASURE.WARRANTY,
  [ProductType.CUSTOM_WORK]: UNIT_MEASURE.CUSTOM_WORK,
  [ProductType.JOINT_POINT]: UNIT_MEASURE.JOINT_POINT,
  [ProductType.OVERSIZE_FEE]: UNIT_MEASURE.OVERSIZE,
  [ProductType.DIAGONAL]: UNIT_MEASURE.DIAGONAL_CORNER,
  [ProductType.OTHER]: UNIT_MEASURE.NONE,
};

export const getUnitType = (priceItem: PricedProduct): string =>
  priceItem.unitOfMeasure
    ? formatUOM(priceItem.unitOfMeasure)
    : formatUOM(
        productTypeToUnitMeasure[priceItem.productType] ??
          UNIT_MEASURE.MATERIALS
      );
